<script setup>
import { computed, nextTick, ref, toRef, watch } from 'vue';
import { useDisclosure } from '@/js/core/composables/useDisclosure';

const props = defineProps({
  id: {
    type: String,
    default: '',
  },
  title: {
    type: String,
    default: '',
  },
  allLabel: {
    type: String,
    default: '',
  },
  options: {
    type: Array,
    default: () => [],
  },
  isOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['toggle']);

const selectedValues = defineModel({
  type: Array,
  default: () => ['all'],
});

const componentRef = ref(null);
const panelId = `courses-filter-panel-${props.id}`;

const { triggerAttrs } = useDisclosure(componentRef, {
  isExpanded: toRef(props, 'isOpen'),
  panelId,
  onClose: () => emit('toggle'),
});

const toggleDropdown = () => {
  emit('toggle');
};

const selectOption = (value, children = []) => {
  const index = selectedValues.value.indexOf(value);
  const isSelecting = index === -1;

  if (value === 'all') {
    selectedValues.value = ['all'];
    return;
  }

  const allIndex = selectedValues.value.indexOf('all');
  if (allIndex > -1) {
    selectedValues.value.splice(allIndex, 1);
  }

  if (isSelecting) {
    selectedValues.value.push(value);
  } else {
    selectedValues.value.splice(index, 1);
  }

  if (children.length > 0) {
    children.forEach(child => {
      const childIndex = selectedValues.value.indexOf(child.id);
      if (isSelecting && childIndex === -1) {
        selectedValues.value.push(child.id);
      } else if (!isSelecting && childIndex > -1) {
        selectedValues.value.splice(childIndex, 1);
      }
    });
  }

  // An empty selection means "no constraint", so surface it as All.
  if (selectedValues.value.length === 0) {
    selectedValues.value = ['all'];
  }
};

// A flat option may be a plain string or a leaf object ({ id, label, disabled }).
// Parent options (nested affiliations) are objects carrying a children array.
const isParentOption = option =>
  typeof option === 'object' && option !== null && Array.isArray(option.children);
const optionLabel = option => (typeof option === 'string' ? option : option.label);
const optionId = option => (typeof option === 'string' ? option : (option.id ?? option.label));

const indicatorState = computed(() => {
  if (selectedValues.value.length === 0) return 'is-none';
  if (selectedValues.value.includes('all')) return 'is-all';
  return 'is-partial';
});

// Dropdown distance from top of viewport. Applied to groups in POSITIONED_GROUPS;
// the value is anchored to the alphabet group's button so all positioned dropdowns
// open at the same viewport y-coordinate as alphabet.
const POSITIONED_GROUPS = ['alphabet', 'course-types'];
const dropdownEl = ref(null);
const positionTopPx = ref(null);

watch(
  () => props.isOpen,
  async isOpen => {
    if (!isOpen || !POSITIONED_GROUPS.includes(props.id)) {
      positionTopPx.value = null;
      return;
    }
    await nextTick();
    const alphabetIndicator = document.querySelector('.indicator--alphabet');
    const alphabetGroup = alphabetIndicator?.closest('.courses-filter-group');
    const reference = alphabetGroup ?? dropdownEl.value;
    if (!reference) return;
    positionTopPx.value = `${reference.getBoundingClientRect().top}px`;
  }
);
</script>

<template>
  <div ref="componentRef" class="courses-filter-group">
    <button
      type="button"
      class="filter-group-label"
      :class="{ 'is-active': isOpen }"
      v-bind="triggerAttrs"
      @click.stop="toggleDropdown"
    >
      <div class="filter-indicator" :class="[`indicator--${id}`, indicatorState]"></div>
      {{ title }}
      <div class="filter-icon">
        <svg viewBox="0 0 501.62 501.62">
          <polygon
            points="250.81 500.5 500.81 0.5 0.81 0.5 250.81 500.5"
            vector-effect="non-scaling-stroke"
          ></polygon>
        </svg>
      </div>
    </button>

    <div
      v-if="isOpen"
      :id="panelId"
      ref="dropdownEl"
      class="filter-dropdown"
      :class="`group--${id}`"
      :style="positionTopPx ? { '--position-top': positionTopPx } : null"
    >
      <div class="filter-dropdown-inner">
        <div
          class="filter-option all-option"
          :class="{ 'is-selected': selectedValues.includes('all') }"
          @click="selectOption('all')"
        >
          <input type="checkbox" :checked="selectedValues.includes('all')" />
          <span>{{ allLabel }}</span>
        </div>

        <fieldset v-if="options.length" class="filter-options">
          <div v-for="(group, gIndex) in options" :key="gIndex" class="filter-group">
            <div v-if="group.sectionTitle" class="filter-section-header">
              {{ group.sectionTitle }}
            </div>

            <div v-for="option in group.choices || [group]" :key="optionId(option)">
              <div
                v-if="!isParentOption(option)"
                class="filter-option"
                :class="{
                  'is-selected': selectedValues.includes(optionId(option)),
                  'is-disabled': option.disabled,
                }"
                @click="!option.disabled && selectOption(optionId(option))"
              >
                <input
                  type="checkbox"
                  :checked="selectedValues.includes(optionId(option))"
                  :disabled="option.disabled"
                  :aria-label="optionLabel(option)"
                />
                <span>{{ optionLabel(option) }}</span>
              </div>

              <div v-else class="nested-group">
                <div
                  class="filter-option parent"
                  :class="{
                    'is-selected': selectedValues.includes(option.id),
                    'is-disabled': option.disabled,
                  }"
                  @click="!option.disabled && selectOption(option.id, option.children)"
                >
                  <input type="checkbox" :checked="selectedValues.includes(option.id)" />
                  <span>{{ option.label }}</span>
                </div>

                <ul v-if="option.children?.length" class="filter-children">
                  <li
                    v-for="child in option.children"
                    :key="child.id"
                    class="filter-option child"
                    :class="{ 'is-selected': selectedValues.includes(child.id) }"
                    @click.stop="selectOption(child.id)"
                  >
                    <input type="checkbox" :checked="selectedValues.includes(child.id)" />
                    <span>{{ child.label }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </fieldset>
      </div>

      <button type="button" class="filter-dropdown-close" @click="emit('toggle')">
        <svg viewBox="0 0 100 100">
          <line x1="0" y1="0" x2="100" y2="100" stroke="black" stroke-width="10" />
          <line x1="100" y1="0" x2="0" y2="100" stroke="black" stroke-width="10" />
        </svg>
      </button>
    </div>
  </div>
</template>
